import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    adminUpdateSite, adminListQuotes, adminUpdateQuote, adminDeleteQuote,
    adminAddGallery, adminDeleteGallery, authLogout,
} from "@/lib/api";
import { useSite } from "@/lib/SiteContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Trash2, Save, Plus, LogOut, ExternalLink, Loader2, Image as ImageIcon } from "lucide-react";

const editableTextFields = [
    { key: "business_name", label: "Business name" },
    { key: "tagline", label: "Tagline" },
    { key: "hero_kicker", label: "Hero kicker (mono label)" },
    { key: "hero_line_1", label: "Hero line 1" },
    { key: "hero_line_2", label: "Hero line 2" },
    { key: "hero_line_3", label: "Hero line 3" },
    { key: "hero_sub", label: "Hero sub-text", type: "textarea" },
    { key: "phone", label: "Phone (display)" },
    { key: "phone_raw", label: "Phone (tel: raw)" },
    { key: "email", label: "Email" },
    { key: "address", label: "Address / area label" },
    { key: "cta_primary", label: "Primary CTA" },
    { key: "cta_secondary", label: "Secondary CTA" },
    { key: "logo_url", label: "Logo URL (hero + share image)" },
    { key: "hero_image_url", label: "Hero background image URL" },
];

export default function Admin() {
    const { user, site, gallery, refreshSite, refreshUser, loading } = useSite();
    const navigate = useNavigate();

    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [galleryUrl, setGalleryUrl] = useState("");
    const [galleryCaption, setGalleryCaption] = useState("");

    // Redirect if not admin
    useEffect(() => {
        if (loading) return;
        if (!user) navigate("/login", { replace: true });
        else if (user.role !== "admin") {
            toast.error("Access denied. Admin role required.");
            navigate("/login", { replace: true });
        }
    }, [user, loading, navigate]);

    useEffect(() => {
        if (site) setForm(site);
    }, [site]);

    useEffect(() => {
        if (user?.role === "admin") loadQuotes();
    }, [user]);

    const loadQuotes = async () => {
        try {
            const list = await adminListQuotes();
            setQuotes(list);
        } catch (e) {
            console.error(e);
        }
    };

    const saveContent = async () => {
        if (!form) return;
        setSaving(true);
        try {
            const payload = {};
            editableTextFields.forEach((f) => (payload[f.key] = form[f.key]));
            payload.service_areas = (typeof form.service_areas === "string"
                ? form.service_areas.split(",").map((s) => s.trim()).filter(Boolean)
                : form.service_areas) || [];
            await adminUpdateSite(payload);
            await refreshSite();
            toast.success("Content updated.");
        } catch (e) {
            toast.error("Failed to save.");
        } finally {
            setSaving(false);
        }
    };

    const handleAddImage = async () => {
        if (!galleryUrl) return toast.error("Paste an image URL.");
        try {
            await adminAddGallery({ url: galleryUrl, caption: galleryCaption });
            setGalleryUrl(""); setGalleryCaption("");
            await refreshSite();
            toast.success("Image added.");
        } catch (e) {
            toast.error("Add failed.");
        }
    };

    const handleDeleteImage = async (id) => {
        if (!window.confirm("Delete this image?")) return;
        try {
            await adminDeleteGallery(id);
            await refreshSite();
            toast.success("Image removed.");
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const handleQuoteStatus = async (id, status) => {
        try {
            await adminUpdateQuote(id, { status });
            await loadQuotes();
        } catch (e) {
            toast.error("Update failed.");
        }
    };

    const handleQuoteDelete = async (id) => {
        if (!window.confirm("Delete this quote?")) return;
        try {
            await adminDeleteQuote(id);
            await loadQuotes();
            toast.success("Quote deleted.");
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const handleLogout = async () => {
        try {
            await authLogout();
            await refreshUser();
            navigate("/login", { replace: true });
        } catch (e) {
            navigate("/login", { replace: true });
        }
    };

    if (loading || !form) {
        return (
            <div className="min-h-screen bg-ink-900 flex items-center justify-center text-white">
                <Loader2 className="animate-spin" />
            </div>
        );
    }
    if (!user || user.role !== "admin") return null;

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const areasValue = Array.isArray(form.service_areas) ? form.service_areas.join(", ") : (form.service_areas || "");

    return (
        <div className="min-h-screen bg-ink-900 text-white">
            {/* Top bar */}
            <div className="border-b border-white/10 bg-ink-900/85 backdrop-blur sticky top-0 z-40">
                <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={form.logo_url} alt="" className="h-9 w-9 object-contain" />
                        <div>
                            <div className="font-display text-lg leading-none">Admin</div>
                            <div className="font-mono-label text-white/50 mt-0.5">{form.business_name}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/" data-testid="admin-view-site" className="btn-ghost !py-2 !px-4 text-sm">
                            View site <ExternalLink size={14} />
                        </Link>
                        <div className="hidden md:flex items-center gap-2 font-mono-label text-white/60">
                            {user.picture && <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />}
                            {user.email}
                        </div>
                        <Button onClick={handleLogout} variant="destructive" size="sm" data-testid="admin-logout">
                            <LogOut size={14} /> Logout
                        </Button>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
                <h1 className="font-display uppercase text-4xl md:text-6xl leading-none mb-8">
                    <span className="text-white">Manage </span>
                    <span className="text-lime-signal">site</span>
                </h1>

                <Tabs defaultValue="content" className="w-full">
                    <TabsList className="bg-ink-800 border border-white/10">
                        <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
                        <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery ({gallery.length})</TabsTrigger>
                        <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes ({quotes.length})</TabsTrigger>
                    </TabsList>

                    {/* CONTENT */}
                    <TabsContent value="content" className="mt-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {editableTextFields.map((f) => (
                                <div key={f.key} className="card-tactical p-5">
                                    <Label htmlFor={f.key} className="font-mono-label text-white/60">{f.label}</Label>
                                    {f.type === "textarea" ? (
                                        <Textarea
                                            id={f.key}
                                            data-testid={`field-${f.key}`}
                                            value={form[f.key] || ""}
                                            onChange={update(f.key)}
                                            rows={3}
                                            className="mt-2 bg-ink-900 border-white/15 text-white"
                                        />
                                    ) : (
                                        <Input
                                            id={f.key}
                                            data-testid={`field-${f.key}`}
                                            value={form[f.key] || ""}
                                            onChange={update(f.key)}
                                            className="mt-2 bg-ink-900 border-white/15 text-white"
                                        />
                                    )}
                                    {f.key === "logo_url" && form.logo_url && (
                                        <img src={form.logo_url} alt="logo preview" className="mt-3 h-16 w-16 object-contain bg-ink-900 border border-white/10 rounded-md" />
                                    )}
                                    {f.key === "hero_image_url" && form.hero_image_url && (
                                        <img src={form.hero_image_url} alt="hero bg preview" className="mt-3 h-24 w-full object-cover border border-white/10 rounded-md" />
                                    )}
                                </div>
                            ))}
                            <div className="card-tactical p-5 lg:col-span-2">
                                <Label className="font-mono-label text-white/60">Service areas (comma separated)</Label>
                                <Input
                                    data-testid="field-service_areas"
                                    value={areasValue}
                                    onChange={(e) => setForm({ ...form, service_areas: e.target.value })}
                                    className="mt-2 bg-ink-900 border-white/15 text-white"
                                />
                            </div>
                        </div>

                        <div className="mt-8 flex items-center justify-end gap-3">
                            <Button onClick={saveContent} disabled={saving} data-testid="btn-save-content" className="bg-lime-signal text-ink-900 hover:bg-lime-signal/90">
                                {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                                Save changes
                            </Button>
                        </div>
                    </TabsContent>

                    {/* GALLERY */}
                    <TabsContent value="gallery" className="mt-6">
                        <div className="card-tactical p-5 md:p-6 mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="md:col-span-2">
                                    <Label className="font-mono-label text-white/60">Image URL</Label>
                                    <Input
                                        value={galleryUrl}
                                        onChange={(e) => setGalleryUrl(e.target.value)}
                                        data-testid="input-gallery-url"
                                        placeholder="https://…"
                                        className="mt-2 bg-ink-900 border-white/15 text-white"
                                    />
                                </div>
                                <div>
                                    <Label className="font-mono-label text-white/60">Caption (optional)</Label>
                                    <Input
                                        value={galleryCaption}
                                        onChange={(e) => setGalleryCaption(e.target.value)}
                                        data-testid="input-gallery-caption"
                                        placeholder="Manicured hedges"
                                        className="mt-2 bg-ink-900 border-white/15 text-white"
                                    />
                                </div>
                            </div>
                            <div className="mt-4 flex justify-end">
                                <Button onClick={handleAddImage} data-testid="btn-add-image" className="bg-lime-signal text-ink-900 hover:bg-lime-signal/90">
                                    <Plus size={16} /> Add image
                                </Button>
                            </div>
                        </div>

                        {gallery.length === 0 ? (
                            <p className="text-white/50 font-mono-label">No images yet. Add one above.</p>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {gallery.map((img) => (
                                    <div key={img.image_id} className="relative group card-tactical overflow-hidden aspect-square" data-testid={`admin-img-${img.image_id}`}>
                                        <img src={img.url} alt={img.caption || "img"} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/95 via-transparent to-transparent" />
                                        <div className="absolute inset-x-0 bottom-0 p-3 flex items-end justify-between gap-2">
                                            <div className="font-mono-label text-white/80 line-clamp-2 text-[10px]">{img.caption || "—"}</div>
                                            <button
                                                onClick={() => handleDeleteImage(img.image_id)}
                                                data-testid={`btn-delete-img-${img.image_id}`}
                                                className="p-2 bg-red-500/90 hover:bg-red-500 rounded-md text-white"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* QUOTES */}
                    <TabsContent value="quotes" className="mt-6">
                        {quotes.length === 0 ? (
                            <div className="card-tactical p-10 text-center">
                                <ImageIcon className="mx-auto text-white/30 mb-3" />
                                <p className="text-white/60">No quote requests yet. When customers submit the contact form, they’ll appear here.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3">
                                {quotes.map((q) => (
                                    <div key={q.quote_id} data-testid={`quote-${q.quote_id}`} className="card-tactical p-5 md:p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div>
                                            <div className="font-mono-label text-white/40 mb-1">Contact</div>
                                            <div className="text-white font-semibold">{q.name}</div>
                                            <div className="text-white/70 text-sm">{q.email}</div>
                                            <div className="text-white/70 text-sm">{q.phone}</div>
                                        </div>
                                        <div>
                                            <div className="font-mono-label text-white/40 mb-1">Details</div>
                                            <div className="text-white/80 text-sm">{q.service || "—"}</div>
                                            <div className="text-white/60 text-sm">{q.address || "—"}</div>
                                        </div>
                                        <div className="md:col-span-1">
                                            <div className="font-mono-label text-white/40 mb-1">Message</div>
                                            <div className="text-white/70 text-sm whitespace-pre-wrap">{q.message || "—"}</div>
                                            <div className="text-white/40 text-xs mt-2">{new Date(q.created_at).toLocaleString()}</div>
                                        </div>
                                        <div className="flex flex-col items-start md:items-end gap-2">
                                            <Badge className={
                                                q.status === "new" ? "bg-lime-signal text-ink-900" :
                                                q.status === "contacted" ? "bg-blue-500 text-white" :
                                                q.status === "closed" ? "bg-white/10 text-white/70" : "bg-white/10"
                                            }>{q.status}</Badge>
                                            <div className="flex flex-wrap gap-2">
                                                {q.status !== "contacted" && (
                                                    <Button size="sm" variant="secondary" onClick={() => handleQuoteStatus(q.quote_id, "contacted")} data-testid={`btn-contacted-${q.quote_id}`}>Mark contacted</Button>
                                                )}
                                                {q.status !== "closed" && (
                                                    <Button size="sm" variant="secondary" onClick={() => handleQuoteStatus(q.quote_id, "closed")} data-testid={`btn-close-${q.quote_id}`}>Close</Button>
                                                )}
                                                <Button size="sm" variant="destructive" onClick={() => handleQuoteDelete(q.quote_id)} data-testid={`btn-del-quote-${q.quote_id}`}>
                                                    <Trash2 size={14} /> Delete
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
